import { useMyLearningPaths } from '@/hooks/queries/learning-paths';
import PromptService from '@/services/prompt-service';
import { Skeleton } from '@nextui-org/react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useCreateOrUpdatePractice } from 'Src/hooks/queries/practices';
import { useAuthStore } from 'Src/store/use-auth-store';
import { PromptData, StandardResponse } from 'Src/types/agent';
import { useMediaQuery } from 'usehooks-ts';
import { getGradientForIndexAsString } from '../../utils/colors';
import DiscoverCard from './components/discover-card';

// LiFT mode tags in order of priority
const LIFT_MODE_TAGS = ['Counselor', 'Action', 'Coach'] as const;

interface PromptWithTag extends PromptData {
  tag?: string;
  tags?: Array<{ value: string } | string>;
}

/**
 * Get the color tag for a prompt based on its tags.
 * Priority:
 * 1. If agent has a LiFT mode tag (Counselor, Action, Coach), use the first one found
 * 2. If agent only has Promotion tag, default to 'Counselor' (Inner Mastery Guide Blue)
 */
function getColorTagForPrompt(task: PromptWithTag): string {
  if (!task.tags || task.tags.length === 0) {
    // No tags array, fall back to legacy behavior or default
    return task.tag || 'Counselor';
  }

  // Extract tag values (handle both string and object formats)
  const tagValues = task.tags.map((t) => (typeof t === 'string' ? t : t.value));

  // Find the first LiFT mode tag in the order they appear in the prompt's tags
  for (const tagValue of tagValues) {
    if (LIFT_MODE_TAGS.includes(tagValue as (typeof LIFT_MODE_TAGS)[number])) {
      return tagValue;
    }
  }

  // No LiFT mode tag found, default to Counselor (Inner Mastery Guide Blue)
  return 'Counselor';
}

const DiscoverPage = () => {
  const orgId = useAuthStore((state) => state.orgId);
  const createPracticeMutation = useCreateOrUpdatePractice();
  const navigate = useNavigate();
  const [loadingPracticeId, setLoadingPracticeId] = useState<number | null>(
    null
  );
  const isMobile = useMediaQuery('(max-width: 768px)');

  const {
    data,
    isLoading: isLoadingLearningPaths,
    isError,
  } = useMyLearningPaths({
    tag: 'Promotion',
  });

  const { data: prompts, isLoading: isLoadingPrompts } = useQuery<
    StandardResponse<PromptData>
  >({
    queryKey: [`agent-promotion`, { is_active: true, tag: 'Promotion' }],
    queryFn: async (): Promise<StandardResponse<PromptData>> => {
      const response = await PromptService.getPrompts({
        filters: {
          page: 1,
          is_active: true,
          tag: 'Promotion',
        },
      });
      return response as StandardResponse<PromptData>;
    },
  });

  // Flatten prompts from learning paths while preserving their tag and tags array
  const scenarios = useMemo((): PromptWithTag[] => {
    if (!data?.results) return [];
    return data.results.flatMap((path) =>
      (path.prompts || []).map((p) => ({
        ...p.prompt,
        tag: path.tag,
        // Preserve the prompt's tags array for color determination
        tags: (p.prompt as PromptWithTag).tags,
      }))
    );
  }, [data]);

  const promptsData = useMemo((): PromptWithTag[] => {
    if (import.meta.env.VITE_LEARNING_PATH_ENABLED === 'true') {
      return scenarios;
    }
    // For non-learning-path mode, preserve tags for color determination
    return (prompts?.results || []).map((p) => ({
      ...p,
      tags: (p as PromptWithTag).tags,
    }));
  }, [prompts, scenarios]);

  const isLoading = useMemo(() => {
    if (import.meta.env.VITE_LEARNING_PATH_ENABLED === 'true') {
      return isLoadingLearningPaths;
    }
    return isLoadingPrompts;
  }, [isLoadingPrompts, isLoadingLearningPaths]);

  const handleScenarioClick = async (
    task: PromptWithTag,
    categoryTag: string
  ) => {
    try {
      setLoadingPracticeId(task.id);
      const response = (await createPracticeMutation.mutateAsync({
        name: task.name,
        org_id: orgId,
      })) as { id: number };

      navigate(
        `../home/${response.id}/run/?action=${encodeURIComponent(task.name)}&id=${response.id}&tag=${encodeURIComponent(categoryTag)}`
      );
    } catch (error) {
      toast.error('Failed to create practice session');
      console.error(error);
    } finally {
      setLoadingPracticeId(null);
    }
  };

  const skeletonCount = isMobile ? 4 : 6;

  if (isError) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <p className="text-gray-500">Error loading content</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col gap-6 pb-24 pt-4 p-2 sm:p-0"
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h1 className="text-2xl font-bold text-gray-900">Suggested for You</h1>
        <p className="text-gray-500 mt-1">
          Explore featured leadership tools and resources
        </p>
      </motion.div>

      {/* Cards Grid */}
      {isLoading ? (
        <div
          className={`${
            isMobile ? 'grid grid-cols-2 gap-4 w-full' : 'flex gap-4 flex-wrap'
          }`}
        >
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Skeleton
              key={`discover-skeleton-${index}`}
              className="w-full md:w-[200px] aspect-square md:h-[200px] shrink-0 min-w-0 rounded-2xl"
            />
          ))}
        </div>
      ) : (
        <div
          className={`${
            isMobile ? 'grid grid-cols-2 gap-4 w-full' : 'flex gap-4 flex-wrap'
          }`}
        >
          {promptsData.map((task, index) => {
            // Get the color tag based on LiFT mode tags, fallback to Counselor
            const colorTag = getColorTagForPrompt(task);
            // Use the learning path tag for navigation, fallback to color tag
            const categoryTag = task.tag || colorTag;
            const bgColor = getGradientForIndexAsString(
              colorTag,
              index,
              true,
              0.75
            );
            const hoverBgColor = getGradientForIndexAsString(
              colorTag,
              index,
              true,
              0.85
            );

            return (
              <DiscoverCard
                key={task.id}
                title={task.name}
                description={task.system_prompt}
                bgColor={bgColor}
                hoverBgColor={hoverBgColor}
                onAction={() => handleScenarioClick(task, categoryTag)}
                isLoading={loadingPracticeId === task.id}
                className={isMobile ? 'w-full min-w-0' : ''}
              />
            );
          })}
        </div>
      )}

      {!promptsData?.length && !isLoading && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-gray-500">No suggestions available</p>
        </div>
      )}
    </motion.div>
  );
};

export default DiscoverPage;
